package dev.kuml.portal.web.editor

/** One peer-cursor colour pair -- [stroke] for the cursor arrow/ring, [flag] for the darker
 * name-flag background behind it (see `app.css`'s `.kportal-presence-flag`). */
public data class PaletteEntry(
    val stroke: String,
    val flag: String,
)

/**
 * R-28: deterministic peer-cursor colour assignment -- FNV-1a-32 hashed over a peer's [forUserId]
 * argument (their `userId`, NOT their per-connection `tokenHex`), so the SAME person keeps the SAME
 * colour across every tab/browser session/reconnect they open on a diagram, rather than a colour
 * that reshuffles on every reconnect the way a `tokenHex`-keyed hash would.
 */
public object PeerCursorPalette {
    private val PALETTE: List<PaletteEntry> =
        listOf(
            PaletteEntry("#D14836", "#9E2F20"),
            PaletteEntry("#C56A15", "#8A4A0C"),
            PaletteEntry("#2E8B3D", "#1E5D28"),
            PaletteEntry("#12867F", "#0B5A55"),
            PaletteEntry("#4A82DC", "#2A55A0"),
            PaletteEntry("#8A5CD1", "#5B3A94"),
            PaletteEntry("#C4478F", "#8B2C62"),
            PaletteEntry("#A87139", "#6C4520"),
        )

    /** FNV-1a-32 over the UTF-8 bytes of [input] -- a small, well-known, dependency-free hash, good
     * enough for an 8-way colour bucket (no cryptographic property needed here). Offset basis
     * `2166136261`, prime `16777619` -- the canonical FNV-1a-32 constants. */
    public fun fnv1a32(input: String): UInt {
        var hash = 2166136261u
        for (byte in input.encodeToByteArray()) {
            hash = hash xor (byte.toUInt() and 0xFFu)
            hash *= 16777619u
        }
        return hash
    }

    /** The palette entry for [userId] -- stable for the same [userId] forever (see class KDoc). */
    public fun forUserId(userId: String): PaletteEntry = PALETTE[(fnv1a32(userId) % PALETTE.size.toUInt()).toInt()]
}
